import { useState, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { UserPen, LogOut, LogIn, Globe, Check, Loader2, Search } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { SUPPORTED_LANGUAGES } from '@/lib/languages';
import logo from '@/assets/igniteaitool-logo.jpg';

interface TopNavProps {
  user?: User | null;
  onLogout?: () => Promise<void>;
}

function getInitials(user: User | null): string {
  if (!user) return '??';
  const displayName = user.user_metadata?.display_name || user.user_metadata?.full_name;
  if (displayName && typeof displayName === 'string') {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  }
  const email = user.email || '';
  return (email.split('@')[0] || '').slice(0, 2).toUpperCase();
}

const TopNav = ({ user, onLogout }: TopNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnAuthPage = location.pathname === '/auth';
  const { language, setLanguage, t, isTranslating } = useLanguage();
  const [langSearch, setLangSearch] = useState('');
  const langInputRef = useRef<HTMLInputElement>(null);

  const filteredLangs = useMemo(() => {
    if (!langSearch.trim()) return SUPPORTED_LANGUAGES;
    const q = langSearch.toLowerCase();
    return SUPPORTED_LANGUAGES.filter(
      l => l.name.toLowerCase().includes(q) || l.nativeName.toLowerCase().includes(q) || l.code.includes(q)
    );
  }, [langSearch]);

  return (
    <nav className="sticky top-0 z-50 h-16 border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto flex h-full max-w-4xl items-center justify-between px-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src={logo}
              alt="IgniteAiTool logo"
              className="h-7 w-7 rounded-full ring-1 ring-border transition-all duration-300 group-hover:ring-primary/50 group-hover:scale-105"
            />
            <span className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
              IgniteAiTool
            </span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline">
            {t('nav.homepage')}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          {/* Language Selector */}
          <DropdownMenu onOpenChange={(open) => { if (open) setTimeout(() => langInputRef.current?.focus(), 50); else setLangSearch(''); }}>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('language.select')}
              >
                {isTranslating ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Globe className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground hidden sm:inline">{t('language.select')}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 p-0">
              <div className="flex items-center border-b px-3 py-2">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <input
                  ref={langInputRef}
                  className="flex h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  placeholder="Search languages..."
                  value={langSearch}
                  onChange={(e) => setLangSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-72 overflow-y-auto p-1">
                {filteredLangs.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className="cursor-pointer flex items-center justify-between"
                  >
                    <span>{lang.nativeName} ({lang.name})</span>
                    {language === lang.code && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
                {filteredLangs.length === 0 && (
                  <div className="px-2 py-3 text-center text-sm text-muted-foreground">No languages found</div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="rounded-full ring-2 ring-border hover:ring-primary/50 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="User menu"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {getInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => navigate('/account')} className="cursor-pointer">
                  <UserPen className="mr-2 h-4 w-4" />
                  {t('nav.editProfile')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onLogout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant={isOnAuthPage ? 'secondary' : 'default'}
              size="sm"
              disabled={isOnAuthPage}
              onClick={() => navigate('/auth')}
              className="gap-2"
            >
              <LogIn className="h-4 w-4" />
              <span className="hidden sm:inline">{t('nav.signIn')}</span>
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
